import React, { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Bot, CheckCircle2, Languages, Sparkles } from "lucide-react";
import api, { apiError, ruleReasons } from "../lib/api";
import { userName, useUsers } from "../lib/hooks";
import { Chip, ReasonsList, ToneChip } from "../components/common";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";

const CANDIDATE_TYPES = {
  conclusion: "Conclusión", decision: "Decisión", accion: "Acción", riesgo: "Riesgo",
  incidencia: "Incidencia", cuestion_abierta: "Cuestión abierta", compromiso: "Compromiso",
  solicitud_info: "Solicitud de información", cambio_potencial: "Cambio potencial", documento: "Documento",
};
const SEMANTIC = {
  comentario: "Comentario", informacion: "Información", propuesta: "Propuesta", recomendacion: "Recomendación",
  intencion: "Intención", compromiso: "Compromiso", accion: "Acción", decision: "Decisión",
};
const BAND = {
  alta: { label: "Alta", tone: "success" }, media: { label: "Media", tone: "warning" }, baja: { label: "Baja", tone: "danger" },
};
const CSTATUS = {
  pendiente: { label: "Pendiente", tone: "warning" }, requiere_definicion: { label: "REQUIERE DEFINICIÓN", tone: "danger" },
  confirmado: { label: "Confirmado", tone: "success" }, corregido: { label: "Corregido", tone: "info" },
  rechazado: { label: "Rechazado", tone: "muted" }, pospuesto: { label: "Pospuesto", tone: "muted" },
};

export default function ReunionValidacion() {
  const { mid } = useParams();
  const users = useUsers();
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [filterType, setFilterType] = useState("todos");
  const [filterBand, setFilterBand] = useState("todas");
  const [dialog, setDialog] = useState(null); // {action, cand}
  const [speakerDialog, setSpeakerDialog] = useState(null); // {sid, current}
  const [speakerName, setSpeakerName] = useState("");
  const [fields, setFields] = useState({});
  const [err, setErr] = useState(null);

  const { data } = useQuery({
    queryKey: ["transcript-ws", mid],
    queryFn: async () => (await api.get(`/reuniones/${mid}/transcript`)).data,
  });
  const { data: meeting } = useQuery({
    queryKey: ["meeting", mid],
    queryFn: async () => (await api.get(`/reuniones/${mid}`)).data,
  });

  const mut = useMutation({
    mutationFn: async ({ cid, action, payload }) => (await api.post(`/reuniones/candidatos/${cid}/accion`, { action, ...payload })).data,
    onSuccess: () => { toast.success("Candidato actualizado"); setDialog(null); setFields({}); setErr(null); qc.invalidateQueries({ queryKey: ["transcript-ws", mid] }); },
    onError: (e) => setErr(e),
  });
  const canManage = (meeting?.actions || []).some((a) => a.permission);
  const analyze = useMutation({
    mutationFn: async () => (await api.post(`/reuniones/${mid}/transcript/analizar`, {})).data,
    onSuccess: (d) => { toast.success(`Análisis IA: ${d.run.candidates_created} candidatos (revisión humana obligatoria)`); qc.invalidateQueries({ queryKey: ["transcript-ws", mid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const finalize = useMutation({
    mutationFn: async () => (await api.post(`/reuniones/${mid}/transcript/validar/finalizar`)).data,
    onSuccess: () => { toast.success("Validación cerrada"); qc.invalidateQueries({ queryKey: ["transcript-ws", mid] }); qc.invalidateQueries({ queryKey: ["meeting", mid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });
  const fixSpeaker = useMutation({
    mutationFn: async ({ sid, speaker }) => (await api.post(`/reuniones/segmentos/${sid}/speaker`, { speaker })).data,
    onSuccess: () => { toast.success("Speaker corregido (valor original conservado); candidatos del segmento desbloqueados"); setSpeakerDialog(null); qc.invalidateQueries({ queryKey: ["transcript-ws", mid] }); },
    onError: (e) => toast.error(apiError(e) || "Error"),
  });

  const cands = useMemo(() => {
    let list = data?.candidates || [];
    if (filterType !== "todos") list = list.filter((c) => c.type === filterType);
    if (filterBand !== "todas") list = list.filter((c) => c.confidence_band === filterBand);
    return list;
  }, [data, filterType, filterBand]);

  const segById = useMemo(() => Object.fromEntries((data?.segments || []).map((s) => [s.id, s])), [data]);
  const selectedSeg = selected ? segById[selected.segment_id] : null;

  const openAction = (cand, action) => { setDialog({ cand, action }); setFields({}); setErr(null); };

  if (!data) return <div className="p-8 text-sm text-slate-500">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="validacion-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/reuniones" className="mb-1 flex items-center gap-1 text-xs text-blue-700 hover:underline" data-testid="back-to-meetings">
            <ArrowLeft className="h-3 w-3" /> Volver a reuniones
          </Link>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="validacion-title">
            Transcript y validación — {meeting?.title || mid}
          </h1>
          <p className="text-sm text-slate-500">IA detecta → humano valida → sistema activa. Ningún candidato se activa automáticamente.</p>
        </div>
        <div className="flex items-center gap-2">
          <ToneChip label={`Validación: ${data.validation_status}`} tone={data.validation_status === "validada" ? "success" : "warning"} testid="validation-status" />
          {data.transcript && canManage && data.validation_status !== "validada" && (
            <Button size="sm" onClick={() => analyze.mutate()} disabled={analyze.isPending} data-testid="analyze-run">
              <Sparkles className="mr-1 h-3.5 w-3.5" /> {data.candidates.length ? "Re-analizar (IA demo)" : "Analizar (IA demo)"}
            </Button>
          )}
          {data.candidates.length > 0 && data.validation_status !== "validada" && canManage && (
            <Button size="sm" variant="outline" onClick={() => finalize.mutate()} disabled={finalize.isPending} data-testid="finalize-validation">
              Cerrar validación
            </Button>
          )}
        </div>
      </div>

      {data.validation_status === "validada" && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800" data-testid="validation-closed-banner">
          Validación cerrada: los candidatos quedan congelados. Para revisar de nuevo, ingiere una nueva versión del transcript.
        </div>
      )}
      {(data.validation_blockers || []).length > 0 && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800" data-testid="validation-blockers">
          {(data.validation_blockers || []).map((b, i) => <p key={i}>· {b.message || b.code}</p>)}
        </div>
      )}

      {!data.transcript ? (
        <div className="rounded-md border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="no-transcript">
          Esta reunión no tiene transcript. El Hito 1 cubre preparación y celebración; la ingesta de transcript se realiza tras la celebración (Teams mock, TXT, manual, notas o audio demo).
        </div>
      ) : (
        <>
          {/* Barra superior: agenda + filtros */}
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="validation-toolbar">
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-52" data-testid="filter-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los tipos</SelectItem>
                {Object.entries(CANDIDATE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterBand} onValueChange={setFilterBand}>
              <SelectTrigger className="w-44" data-testid="filter-band"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Toda confianza</SelectItem>
                <SelectItem value="alta">Alta</SelectItem>
                <SelectItem value="media">Media</SelectItem>
                <SelectItem value="baja">Baja</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-xs text-slate-400">
              v{data.versions[0]?.number} · {(data.versions[0]?.languages || []).join("/")} · {data.segments.length} segmentos · {data.candidates.length} candidatos
              · umbrales: alta ≥ {data.thresholds.alta}, media ≥ {data.thresholds.media}
            </span>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            {/* Izquierda: transcript */}
            <div className="space-y-2" data-testid="transcript-pane">
              <p className="text-sm font-semibold text-slate-800"><Languages className="mr-1 inline h-3.5 w-3.5" />Transcript (fuente original conservada)</p>
              <div className="max-h-[60vh] space-y-1.5 overflow-y-auto rounded-md border border-slate-200 bg-white p-3">
                {data.segments.map((s) => {
                  const lowSpk = s.speaker_confidence < data.thresholds.media && !s.speaker_confirmed;
                  return (
                    <div key={s.id}
                      className={`cursor-pointer rounded border px-2.5 py-2 text-sm transition-colors ${selectedSeg?.id === s.id ? "border-blue-400 bg-blue-50" : "border-slate-100 hover:border-slate-300"}`}
                      onClick={() => setSelected(data.candidates.find((c) => c.segment_id === s.id) || selected)}
                      data-testid={`segment-${s.id}`}>
                      <div className="flex items-center justify-between text-xs text-slate-400">
                        <span className="font-mono">{s.t_start}–{s.t_end} · {s.lang.toUpperCase()}</span>
                        <span className="flex items-center gap-1">
                          <span className={lowSpk ? "font-semibold text-red-600" : "text-slate-500"}>
                            {s.speaker_confirmed || s.speaker_detected}
                            {lowSpk && ` (conf. ${s.speaker_confidence.toFixed(2)} — validar)`}
                          </span>
                          {lowSpk && (
                            <button className="text-blue-700 underline" data-testid={`speaker-fix-${s.id}`}
                              onClick={(e) => { e.stopPropagation(); setSpeakerDialog({ sid: s.id, current: s.speaker_detected }); setSpeakerName(""); }}>
                              corregir
                            </button>
                          )}
                        </span>
                      </div>
                      <p className="mt-0.5 text-slate-700">{s.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Derecha: candidatos */}
            <div className="space-y-2" data-testid="candidates-pane">
              <p className="text-sm font-semibold text-slate-800"><Bot className="mr-1 inline h-3.5 w-3.5" />Candidatos detectados por IA ({cands.length})</p>
              <div className="max-h-[60vh] space-y-2 overflow-y-auto">
                {cands.map((c) => (
                  <div key={c.id}
                    className={`rounded-md border bg-white p-3 ${selected?.id === c.id ? "border-blue-400" : "border-slate-200"}`}
                    data-testid={`candidate-${c.id}`}>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <ToneChip label={CANDIDATE_TYPES[c.type] || c.type} tone={c.type === "decision" ? "ai" : c.type === "riesgo" || c.type === "incidencia" ? "warning" : "info"} testid={`cand-type-${c.id}`} />
                      <ToneChip label={SEMANTIC[c.semantic_class] || c.semantic_class} tone="muted" />
                      <Chip map={BAND} value={c.confidence_band} testid={`cand-band-${c.id}`} />
                      <Chip map={CSTATUS} value={c.status} testid={`cand-status-${c.id}`} />
                      {c.speaker_low_confidence && <ToneChip label="speaker dudoso" tone="danger" testid={`cand-lowspk-${c.id}`} />}
                    </div>
                    <p className="mt-1.5 cursor-pointer text-sm text-slate-800" onClick={() => setSelected(c)}>{(c.human_values?.corrected_text) || c.proposal}</p>
                    <p className="mt-1 text-xs text-violet-600">{c.disclaimer}</p>
                    <p className="mt-1 text-xs text-slate-500">
                      t={c.timestamp} · speaker: {c.speaker} · conf. {c.confidence.toFixed(2)}
                      {c.agenda_item_id && ` · punto ${c.agenda_item_id}`}
                    </p>
                    <p className="mt-0.5 text-xs italic text-slate-400" data-testid={`cand-explain-${c.id}`}>{c.explanation}</p>
                    {c.materialized?.entity_id && (
                      <p className="mt-1 text-xs text-emerald-700" data-testid={`cand-materialized-${c.id}`}>
                        <CheckCircle2 className="mr-1 inline h-3 w-3" />Activado como {c.materialized.entity_type}: {c.materialized.entity_id}
                      </p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {(c.actions || []).filter((a) => a.allowed && ["confirmar", "corregir", "rechazar", "cambiar_categoria", "asignar_responsable", "asignar_fecha", "relacionar_entidad", "marcar_pendiente"].includes(a.action)).map((a) => (
                        <Button key={a.action} size="sm" variant="outline" className="h-7 px-2 text-xs"
                          data-testid={`cand-${a.action}-${c.id}`} onClick={() => openAction(c, a.action)}>
                          {{ confirmar: "Confirmar", corregir: "Corregir", rechazar: "Rechazar", cambiar_categoria: "Categoría", asignar_responsable: "Responsable", asignar_fecha: "Fecha", relacionar_entidad: "Relacionar", marcar_pendiente: "Pendiente" }[a.action]}
                        </Button>
                      ))}
                      {(c.actions || []).filter((a) => !a.allowed && (a.reasons || []).length && a.action === "confirmar" && ["pendiente", "requiere_definicion", "pospuesto"].includes(c.status) && !(a.reasons || []).some((r) => r.code === "validacion_cerrada")).map((a) => (
                        <span key={a.action} className="text-xs text-red-600" title={(a.reasons || []).map((r) => r.message).join(" · ")} data-testid={`cand-blocked-confirmar-${c.id}`}>
                          Confirmar bloqueado: {(a.reasons || []).map((r) => r.message).join(" · ")}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                {!cands.length && <p className="text-sm text-slate-400">Sin candidatos con estos filtros.</p>}
              </div>
            </div>
          </div>

          {/* Compromisos y cuestiones abiertas materializados */}
          {(data.commitments.length > 0 || data.questions.length > 0) && (
            <div className="grid gap-4 md:grid-cols-2">
              {data.commitments.length > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50/50 p-3" data-testid="commitments-panel">
                  <p className="text-sm font-semibold text-slate-800">Compromisos de reunión</p>
                  {data.commitments.map((c) => (
                    <div key={c.id} className="mt-2 rounded border border-amber-100 bg-white p-2" data-testid={`commitment-${c.id}`}>
                      <p className="text-sm">{c.text}</p>
                      <p className="mt-1 text-xs font-semibold text-amber-700">{c.legal_warning}</p>
                      <p className="text-xs text-slate-500">Responsable: {userName(users.map, c.responsible_id)} · Estado: {c.status}</p>
                    </div>
                  ))}
                </div>
              )}
              {data.questions.length > 0 && (
                <div className="rounded-md border border-slate-200 bg-white p-3" data-testid="questions-panel">
                  <p className="text-sm font-semibold text-slate-800">Cuestiones abiertas</p>
                  {data.questions.map((q) => (
                    <div key={q.id} className="mt-2 rounded border border-slate-100 p-2" data-testid={`question-${q.id}`}>
                      <p className="text-sm">{q.question}</p>
                      <p className="text-xs text-slate-500">Responsable: {userName(users.map, q.responsible_id)} · Estado: {q.status}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Diálogo de corrección de speaker */}
      <Dialog open={!!speakerDialog} onOpenChange={() => setSpeakerDialog(null)}>
        <DialogContent data-testid="speaker-fix-dialog">
          <DialogHeader><DialogTitle>Corregir speaker (segmento)</DialogTitle></DialogHeader>
          {speakerDialog && (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Detectado por IA: «{speakerDialog.current}» — el valor original se conserva en el historial.</p>
              <div><Label>Speaker confirmado *</Label><Input value={speakerName} onChange={(e) => setSpeakerName(e.target.value)} data-testid="field-speaker" /></div>
              <Button data-testid="speaker-fix-submit" disabled={!speakerName.trim() || fixSpeaker.isPending}
                onClick={() => fixSpeaker.mutate({ sid: speakerDialog.sid, speaker: speakerName.trim() })}>
                Confirmar speaker
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de acción sobre candidato */}
      <Dialog open={!!dialog} onOpenChange={() => setDialog(null)}>
        <DialogContent data-testid="candidate-action-dialog">
          <DialogHeader><DialogTitle>{dialog?.action} — {CANDIDATE_TYPES[dialog?.cand?.type]}</DialogTitle></DialogHeader>
          {dialog && (
            <div className="space-y-3">
              <p className="rounded bg-slate-50 p-2 text-xs text-slate-600">{(dialog.cand.human_values?.corrected_text) || dialog.cand.proposal}</p>
              {dialog.action === "corregir" && (
                <div><Label>Texto corregido *</Label><Textarea value={fields.corrected_text || ""} onChange={(e) => setFields({ ...fields, corrected_text: e.target.value })} data-testid="field-corrected_text" /></div>
              )}
              {dialog.action === "rechazar" && (
                <div><Label>Motivo del rechazo *</Label><Textarea value={fields.motivo || ""} onChange={(e) => setFields({ ...fields, motivo: e.target.value })} data-testid="field-motivo" /></div>
              )}
              {dialog.action === "cambiar_categoria" && (
                <div><Label>Nueva categoría *</Label>
                  <Select value={fields.new_type || ""} onValueChange={(v) => setFields({ ...fields, new_type: v })}>
                    <SelectTrigger data-testid="field-new_type"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{Object.entries(CANDIDATE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dialog.action === "cambiar_categoria" && ["decision", "accion", "riesgo", "incidencia", "compromiso", "cambio_potencial"].includes(fields.new_type) && (
                <div>
                  <Label>Motivo de la recategorización * (categoría material)</Label>
                  <Textarea value={fields.motivo || ""} onChange={(e) => setFields({ ...fields, motivo: e.target.value })} data-testid="field-motivo" />
                  <p className="mt-1 text-xs text-amber-700">Elevar a una categoría material exige justificación humana registrada.</p>
                </div>
              )}
              {dialog.action === "asignar_responsable" && (
                <div><Label>Responsable *</Label>
                  <Select value={fields.responsible_id || ""} onValueChange={(v) => setFields({ ...fields, responsible_id: v })}>
                    <SelectTrigger data-testid="field-responsible_id"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                    <SelectContent>{(users.users || []).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              {dialog.action === "asignar_fecha" && (
                <div><Label>Fecha *</Label><Input type="date" value={(fields.due_date || "").slice(0, 10)} onChange={(e) => setFields({ ...fields, due_date: e.target.value })} data-testid="field-due_date" /></div>
              )}
              {dialog.action === "relacionar_entidad" && (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>Tipo *</Label>
                    <Select value={fields.entity_type || ""} onValueChange={(v) => setFields({ ...fields, entity_type: v })}>
                      <SelectTrigger data-testid="field-entity_type"><SelectValue placeholder="Tipo" /></SelectTrigger>
                      <SelectContent>{["contract", "project", "certification", "change", "risk", "document"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>ID entidad *</Label><Input value={fields.entity_id || ""} onChange={(e) => setFields({ ...fields, entity_id: e.target.value })} placeholder="ctr_..." data-testid="field-entity_id" /></div>
                </div>
              )}
              {dialog.action === "confirmar" && dialog.cand.type === "accion" && (
                <>
                  <div><Label>Resultado esperado</Label><Input value={fields.expected_result || ""} onChange={(e) => setFields({ ...fields, expected_result: e.target.value })} data-testid="field-expected_result" /></div>
                  <div><Label>Evidencia requerida</Label><Input value={fields.evidence_required || ""} onChange={(e) => setFields({ ...fields, evidence_required: e.target.value })} data-testid="field-evidence_required" /></div>
                </>
              )}
              {err && (ruleReasons(err) ? <ReasonsList reasons={ruleReasons(err)} /> : <p className="rounded border border-red-200 bg-red-50 p-2 text-sm text-red-700" data-testid="candidate-error">{apiError(err)}</p>)}
              <Button data-testid="candidate-action-submit" disabled={mut.isPending}
                onClick={() => {
                  const payload = { ...fields };
                  if (payload.due_date) payload.due_date = new Date(payload.due_date).toISOString();
                  mut.mutate({ cid: dialog.cand.id, action: dialog.action, payload });
                }}>
                Aplicar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
